/**
 * The three.js half of the ship wake: one fixed-capacity mesh holding every
 * sloop's bow V and stern trail, filled from `flight/ship-wake.ts`'s arithmetic.
 *
 * `SearchlightBeams`' shape — `begin` / `push` / `end`, a class because it owns
 * mutable buffers, hidden outright when nothing was written so an empty pool
 * costs no draw call at all — with one structural difference: a wake is not a
 * repeated object placed by a matrix, it is a different curve every frame, so
 * this is a plain indexed `Mesh` whose position attribute is rewritten rather
 * than an `InstancedMesh`.
 *
 * ## One mesh for the whole fleet
 *
 * `SHIP_MAX_COUNT` ships × three ribbons × 288 triangles is 864 triangles and
 * **one draw call**, because the index buffer is built once for the full
 * capacity and `setDrawRange` publishes only the slots that were pushed. Ships
 * fill slots in push order, so a sortie with one ship draws a third of the
 * indices and the untouched two thirds of the position buffer are never read.
 *
 * ## The colours are written once and never again
 *
 * `writeWakeColours` depends only on which station and lane a vertex is, so the
 * whole colour attribute is filled in the constructor. Per frame this pool
 * touches exactly one buffer — positions — which is what keeps a wake at the
 * cost of a `Float32Array` walk over 675 vertices a ship.
 *
 * ## It draws on her waterline, which is the sea's own surface
 *
 * `SHIP_WATERLINE_Y_M` — the plane `ShipModel.setPose` floats the hull on, a
 * metre below the datum because that is where `world/water-mask.ts` draws the
 * sea plate. The wake has to leave her stern exactly at her marks, so it uses
 * the same constant rather than a second opinion about where the water is; the
 * two-centimetre ladder between the three ribbons is the only lift in it, and
 * `WAKE_RIBBON_LIFT_M` says why.
 */

import * as THREE from "three/webgpu";
import { SHIP_MAX_COUNT } from "@red-opus/engine";
import { SHIP_WATERLINE_Y_M } from "./ship-mounts.ts";
import {
  FOAM_RAMP,
  WAKE_INDEX_COUNT,
  WAKE_VERTEX_COUNT,
  wakeIndices,
  writeWakeColours,
  writeWakePositions,
  type WakePoseAt,
} from "../flight/ship-wake.ts";

/** A fixed-capacity pool of wakes: one mesh, one draw call, one buffer touched a frame. */
export class ShipWakeRibbons {
  readonly mesh: THREE.Mesh;
  private readonly positions: THREE.BufferAttribute;
  private readonly vertices: Float32Array;
  private written = 0;

  constructor(capacity: number = SHIP_MAX_COUNT) {
    const geometry = new THREE.BufferGeometry();
    this.vertices = new Float32Array(capacity * WAKE_VERTEX_COUNT * 3);
    this.positions = new THREE.BufferAttribute(this.vertices, 3);
    this.positions.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute("position", this.positions);

    const colours = new Float32Array(capacity * WAKE_VERTEX_COUNT * 3);
    const oneShip = new Float32Array(WAKE_VERTEX_COUNT * 3);
    writeWakeColours(oneShip, FOAM_RAMP);
    for (let slot = 0; slot < capacity; slot += 1) colours.set(oneShip, slot * WAKE_VERTEX_COUNT * 3);
    geometry.setAttribute("color", new THREE.BufferAttribute(colours, 3));

    const template = wakeIndices();
    const indices = new Uint32Array(capacity * WAKE_INDEX_COUNT);
    for (let slot = 0; slot < capacity; slot += 1) {
      const base = slot * WAKE_VERTEX_COUNT;
      for (let at = 0; at < WAKE_INDEX_COUNT; at += 1) {
        indices[slot * WAKE_INDEX_COUNT + at] = (template[at] ?? 0) + base;
      }
    }
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.setDrawRange(0, 0);

    // Opaque and vertex-coloured, exactly like the aeroplane fallback material
    // in `flight/aircraft-mesh.ts`. **No transparency anywhere**: the fade is a
    // walk down `BARON.PAL` toward the water's own entry, which is what a
    // 256-colour game would have done and what keeps this mesh out of the
    // reversed-Z trap that draws a `transparent` mesh as zero pixels
    // (`combat-visuals.ts`).
    this.mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }));
    // The wakes are kilometres from the origin and move every frame; a bounding
    // sphere computed once would cull the whole mesh permanently. Every pool in
    // this renderer makes the same call.
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  begin(): void {
    this.written = 0;
  }

  /**
   * Lays one ship's wake, from her past poses and the speed she is making.
   *
   * Returns `false` when nothing was drawn — the pool is full, or she is not
   * under way — which a caller may treat the same way. Nothing is written in
   * that case, so a stopped ship leaves the buffer alone rather than leaving a
   * collapsed wake in it: the slot is simply never published.
   */
  push(poseAt: WakePoseAt, speedMps: number): boolean {
    if (this.written >= this.capacity) return false;
    // Written straight into the pool's own buffer at this slot's offset. A
    // `Float32Array` view per ship per frame would be 180 allocations a second
    // for a picture that already costs nothing.
    const wrote = writeWakePositions(
      this.vertices,
      this.written * WAKE_VERTEX_COUNT,
      poseAt,
      speedMps,
      SHIP_WATERLINE_Y_M,
    );
    if (!wrote) return false;
    this.written += 1;
    return true;
  }

  /** Publishes this frame's writes. */
  end(): void {
    this.mesh.geometry.setDrawRange(0, this.written * WAKE_INDEX_COUNT);
    this.mesh.visible = this.written > 0;
    this.positions.needsUpdate = true;
  }

  /** Ships whose wake the last `end()` published — what the draw-call count and the tests read. */
  get liveCount(): number {
    return this.written;
  }

  get capacity(): number {
    return this.positions.count / WAKE_VERTEX_COUNT;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    // Freeing the material is what releases this render object's bindings under
    // the WebGPU backend (2026-08-19 world-stability lesson), not bookkeeping.
    (this.mesh.material as THREE.Material).dispose();
  }
}
